import { create } from "zustand";
import { persist } from "zustand/middleware";
import { logout, verifyTokenService } from "../services/authService";
import { usePushStore } from "./notificationStore";
import { User } from "../types/push";

interface AuthState {
  user: User | null;
  username: string | null;
  email: string | null;
  profilePicture: string | null;
  message: string;
  isAuthenticated: boolean;
  isLoggedIn: boolean;
  authState: "notChecked" | "checking" | "valid" | "invalid";
  error: string | null;
  successMessage: string | null;
  isLoading: boolean;
  subscribed: boolean;
  role: string | null;
  isLogged: boolean;
  lastVerifiedAt: number;
  verifyToken: () => Promise<{ isValid: boolean; role?: string | null; user?: User | null }>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      username: null,
      email: null,
      profilePicture: null,
      message: "",
      isAuthenticated: false,
      isLoggedIn: true,
      authState: "notChecked",
      error: null,
      successMessage: null,
      isLoading: false,
      subscribed: false,
      role: null,
      isLogged: false,
      lastVerifiedAt: 0,

      verifyToken: async (): Promise<{ isValid: boolean; role?: string | null; user?: User | null }> => {
        set({ isLoading: true, error: null });
        try {
          const result = await verifyTokenService();
          const responseData = result?.data;
      
          if (responseData?.code === 1040) {
            const newUser: User = responseData.result;
            const role = newUser.userMetadata?.role || null;
      
            set((state) => {
              const updatedSubscribed = newUser.subscribed;
      
              if (updatedSubscribed && !state.subscribed && "serviceWorker" in navigator) {
                navigator.serviceWorker.ready.then((registration) => {
                  usePushStore.getState().subscribeToPush(registration);
                });
              }
              return {
                user: newUser,
                username: newUser.username,
                email: newUser.email,
                profilePicture: newUser.profilePicture,
                subscribed: updatedSubscribed,
                role,
                isAuthenticated: true,
                authState: "valid",
              };
            });
      
            set({ isLoading: false, isLogged: true, lastVerifiedAt: Date.now() });
            return { isValid: true, role, user: newUser };
          } else {
            document.cookie = "access_token=; Max-Age=0; path=/";
            set({
              user: null,
              username: null,
              email: null,
              role: null,
              error: responseData?.message || "Invalid token",
              authState: "invalid",
              isLoading: false,
              lastVerifiedAt: Date.now(),
            });
          return { isValid: false, role: null, user: null };
          }
        } catch (error) {
          console.error("💥 Error during token verification:", error);
          set({
            error: "An error occurred during token verification",
            authState: "invalid",
            isLoading: false,
            role: null,
            lastVerifiedAt: Date.now(),
          });
          return { isValid: false, role: null, user: null };
        }
      },
      
      logout: async () => {
        try {
          const responseCode = await logout();
          if (responseCode === 1005) {
            document.cookie = "access_token=; Max-Age=0; path=/; domain=" + window.location.hostname + "; Secure; SameSite=Lax";
            localStorage.removeItem("auth-storage");
            sessionStorage.clear();
            await useAuthStore.persist.clearStorage();
            set({
              user: null,
              username: null,
              email: null,
              profilePicture: null,
              isAuthenticated: false,
              authState: "invalid",
              isLoading: false,
              isLogged: false,
            });
          } else {
            console.warn("Logout response code did not match expected value:", responseCode);
          }
        } catch (error) {
          console.error("Logout failed:", error);
        }
      },
    }),
    { name: "auth-storage" }
  )
);
